import api from '@api/endpoints';
import SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS from '@store/constants/settlementServiceDashboardConstants';
import { setAlert } from '@store/actions/alertActions';
import { ALERT_STATUS_TYPE } from '@constants';
import { accountSettlementsConfig } from '@views/SettlementServiceDashboard/utils';
import { 
  updateSettlementServiceLedgersReqData,
  updateSettlementServiceClientsReqData,
  updateSettlementServiceAccountsReqData
} from '@store/actions/reqDataActions';


export const getDashboardConfig = ({onSuccess, onComplete}) => async (dispatch) => {
  try {
    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GETTING_COMMONS_CONFIG
    });

    const { data } = await api.settlementServiceDashboard.getDashboardConfig();

    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GET_COMMONS_CONFIG_SUCCESS,
      payload: data?.common
    });

    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(
      setAlert({
        title: error?.message || 'Error while fetching clients!',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );
    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GET_COMMONS_CONFIG_FAILURE,
      payload: error.response?.data || error.message
    });
  } finally {
    onComplete && onComplete();
  }
};
// Client Management Actions
export const getAllClients = ({ payload, onSuccess, onComplete }) => async (dispatch) => {
  try {
    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GETTING_CLIENTS
    });

    const { data } = await api.settlementServiceDashboard.getAllClients({ payload });

    const clientData = data?.content || data?.data || (Array.isArray(data) ? data : []);

    const paginationData = {
      pageSize: data?.size || data?.pageSize || 20,
      page: data?.number || data?.page || 0,
      totalPages: data?.totalPages || Math.ceil((data?.totalElements || 0) / (data?.size || 20)),
      totalElements: data?.totalElements || 0
    };

    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GET_CLIENTS_SUCCESS,
      payload: clientData
    });

    // Update pagination data in reqData reducer
    dispatch(
      updateSettlementServiceClientsReqData({
        totalPages: paginationData.totalPages,
        pageSize: paginationData.pageSize,
        pageNumber: paginationData.page
      })
    );

    onSuccess && onSuccess({ ...paginationData, data: clientData });
  } catch (error) {
    dispatch(
      setAlert({
        title: error?.message || 'Error while fetching clients!',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );
    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GET_CLIENTS_FAILURE,
      payload: error.response?.data || error.message
    });
  } finally {
    onComplete && onComplete();
  }
};

export const getClientDetails = ({ clientId, onSuccess, onComplete }) => async (dispatch) => {
  try {
    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GETTING_CLIENT_DETAILS
    });

    const { data } = await api.settlementServiceDashboard.getClientDetails({ clientId });

    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GET_CLIENT_DETAILS_SUCCESS,
      payload: data
    });
    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(
      setAlert({
        title: error?.message || 'Error while fetching client details!',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );
    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GET_CLIENT_DETAILS_FAILURE,
      payload: error.response?.data || error.message
    });
  } finally {
    onComplete && onComplete();
  }
};

export const addNewClientSettlement = ({ payload, onSuccess, onComplete }) => async (dispatch) => {
  try {
    const { data } = await api.settlementServiceDashboard.addNewClientSettlement({ payload });

    dispatch(
      setAlert({
        title: "Client Settlement Successfully Added!",
        status: ALERT_STATUS_TYPE.SUCCESS
      })
    );

    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(
      setAlert({
        title: error?.message || 'Error while creating client settlement!',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );
  } finally {
    onComplete && onComplete();
  }
};

export const updateClientSettlement = ({ clientId, payload, onSuccess, onComplete }) => async (dispatch) => {
  try {
    const { data } = await api.settlementServiceDashboard.updateClientSettlement({ clientId, payload });

    dispatch(
      setAlert({
        title: "Client Settlement Successfully Updated!",
        status: ALERT_STATUS_TYPE.SUCCESS
      })
    );

    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(
      setAlert({
        title: error?.message || 'Error while updating client settlement!',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );
  } finally {
    onComplete && onComplete();
  }
};

export const resetClientsData = () => ({
  type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.RESET_CLIENTS_DATA
});

export const resetClientDetailsData = () => ({
  type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.RESET_CLIENT_DETAILS_DATA
});


// Ledger Management Actions
export const getAllLedgers = ({ payload, onSuccess, onComplete }) => async (dispatch) => {
  try {
    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GETTING_LEDGERS
    });

    const { data } = await api.settlementServiceDashboard.getAllLedgers({ payload });

    const ledgerData = data?.content || data?.data || (Array.isArray(data) ? data : []);

    const paginationData = {
      pageSize: data?.size || data?.pageSize || 20,
      page: data?.number || data?.page || 0,
      totalPages: data?.totalPages || Math.ceil((data?.totalElements || 0) / (data?.size || 20)),
      totalElements: data?.totalElements || 0
    };

    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GET_LEDGERS_SUCCESS,
      payload: ledgerData
    });

    dispatch(
      updateSettlementServiceLedgersReqData({
        totalPages: paginationData.totalPages,
        pageSize: paginationData.pageSize,
        pageNumber: paginationData.page
      })
    );

    onSuccess && onSuccess({ ...paginationData, data: ledgerData });
  } catch (error) {
    dispatch(
      setAlert({
        title: error?.message || 'Error while fetching ledgers!',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );

    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GET_LEDGERS_FAILURE,
      payload: error.response?.data || error.message
    });
  } finally {
    onComplete && onComplete();
  }
};

export const getLedgerDetails = ({ ledgerId, onSuccess, onComplete }) => async (dispatch) => {
  try {
    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GETTING_LEDGER_DETAILS
    });

    const { data } = await api.settlementServiceDashboard.getLedgerDetails({ ledgerId });

    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GET_LEDGER_DETAILS_SUCCESS,
      payload: data
    });

    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(
      setAlert({
        title: error?.message || 'Error while fetching ledger details!',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );

    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GET_LEDGER_DETAILS_FAILURE,
      payload: error.response?.data || error.message
    });
  } finally {
    onComplete && onComplete();
  }
};

export const addNewLedgerSettlement = ({ payload, onSuccess, onComplete }) => async (dispatch) => {
  try {
    const { data } = await api.settlementServiceDashboard.addNewLedgerSettlement({ payload });

    dispatch(
      setAlert({
        title: "Ledger Settlement Successfully Added!",
        status: ALERT_STATUS_TYPE.SUCCESS
      })
    );

    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(
      setAlert({
        title: error?.message || 'Error while creating ledger settlement!',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );
  } finally {
    onComplete && onComplete();
  }
};

export const resetLedgersData = () => ({
  type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.RESET_LEDGERS_DATA
});

export const resetLedgerDetailsData = () => ({
  type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.RESET_LEDGER_DETAILS_DATA
});


// Account Operations Actions
export const getAllAccounts = ({ payload, onSuccess, onComplete }) => async (dispatch) => {
  try {
    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GETTING_ACCOUNTS
    });

    const { data } = await api.settlementServiceDashboard.getAllAccounts({ payload });

    const accountData = data?.content || data?.data || (Array.isArray(data) ? data : []);

    const paginationData = {
      pageSize: data?.size || data?.pageSize || 20,
      page: data?.number || data?.page || 0,
      totalPages: data?.totalPages || Math.ceil((data?.totalElements || 0) / (data?.size || 20)),
      totalElements: data?.totalElements || 0
    };

    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GET_ACCOUNTS_SUCCESS,
      payload: accountSettlementsConfig.entityConvertor(accountData)
    });

    // Update pagination data in reqData reducer
    dispatch(
      updateSettlementServiceAccountsReqData({
        totalPages: paginationData.totalPages,
        pageSize: paginationData.pageSize,
        pageNumber: paginationData.page
      })
    );

    onSuccess && onSuccess({ ...paginationData, data: accountData });
  } catch (error) {
    dispatch(
      setAlert({
        title: error?.message || 'Error while fetching accounts!',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );
    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GET_ACCOUNTS_FAILURE,
      payload: error.response?.data || error.message
    });
  } finally {
    onComplete && onComplete();
  }
};

export const getAccountDetails = ({ accountId, onSuccess, onComplete }) => async (dispatch) => {
  try {
    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GETTING_ACCOUNT_DETAILS
    });

    const { data } = await api.settlementServiceDashboard.getAccountDetails({ accountId });

    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GET_ACCOUNT_DETAILS_SUCCESS,
      payload: data
    });

    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(
      setAlert({
        title: error?.message || 'Error while fetching account details!',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );
    dispatch({
      type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.GET_ACCOUNT_DETAILS_FAILURE,
      payload: error.response?.data || error.message
    });
  } finally {
    onComplete && onComplete();
  }
};

export const addNewAccountSettlement = ({ payload, onSuccess, onComplete }) => async (dispatch) => {
  try {
    const { data } = await api.settlementServiceDashboard.addNewAccountSettlement({ payload });

    dispatch(
      setAlert({
        title: "Account Settlement Successfully Added!",
        status: ALERT_STATUS_TYPE.SUCCESS
      })
    );

    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(
      setAlert({
        title: error?.message || 'Error while creating account settlement!',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );
  } finally {
    onComplete && onComplete();
  }
};

export const updateAccountSettlement = ({ accountId, payload, onSuccess, onComplete }) => async (dispatch) => {
  try {
    const { data } = await api.settlementServiceDashboard.updateAccountSettlement({ accountId, payload });

    dispatch(
      setAlert({
        title: "Account Settlement Successfully Updated!",
        status: ALERT_STATUS_TYPE.SUCCESS
      })
    );

    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(
      setAlert({
        title: error?.message || 'Error while updating account settlement!',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );
  } finally {
    onComplete && onComplete();
  }
};

export const resetAccountsData = () => ({
  type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.RESET_ACCOUNTS_DATA
});

export const resetAccountDetailsData = () => ({
  type: SETTLEMENT_SERVICE_DASHBOARD_CONSTANTS.RESET_ACCOUNT_DETAILS_DATA
});